/**
 * Doserne Progress Timeline (reusable component).
 * Renders photo posts grouped by date for a given project.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Camera, Trash2, User as UserIcon, Package, Check, Truck, Clock, ExternalLink, ChevronLeft, ChevronRight, X } from "lucide-react";
import MaterialListModal from "./MaterialListModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString("no-NO", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
  } catch { return iso; }
}

export default function DoserneProgressTimeline({ projectId, authHeader, currentUserId, isAdmin }) {
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lightbox, setLightbox] = useState(null); // { photos: string[], index: number }
  // Deep-link: opens the auto-mirrored material-list in the shared
  // editor when the user taps the Package button on a progress card.
  const [materialListId, setMaterialListId] = useState(null);
  const [jobs, setJobs] = useState([]);
  const [jobFilter, setJobFilter] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/doserne/progress/by-project/${projectId}`, authHeader());
      setDays(data.days || []);
      try {
        const jr = await axios.get(`${API}/doserne/projects/${projectId}/jobs`, authHeader());
        setJobs(jr.data.jobs || []);
      } catch { /* silent */ }
    } catch { toast.error("Kunne ikke laste progresjon"); }
    setLoading(false);
  };
  useEffect(() => { if (projectId) load(); }, [projectId]);

  const del = async (postId) => {
    if (!window.confirm("Slette dette innlegget?")) return;
    try {
      await axios.delete(`${API}/doserne/progress/${postId}`, authHeader());
      toast.success("Slettet");
      load();
    } catch { toast.error("Kunne ikke slette"); }
  };

  // Cycle: pending → ordered → delivered → pending. Optimistically
  // update local state, then persist. Any member can toggle — makes
  // the checklist a living document instead of an admin-only gate.
  const cycleMaterial = async (postId, matId, currentStatus) => {
    const order = ["pending", "ordered", "delivered"];
    const next = order[(order.indexOf(currentStatus) + 1) % order.length];
    setDays((prev) => prev.map((d) => ({
      ...d,
      posts: d.posts.map((p) => p.id !== postId ? p : {
        ...p,
        materials: (p.materials || []).map((m) => m.id === matId ? { ...m, status: next } : m),
      }),
    })));
    try {
      await axios.patch(
        `${API}/doserne/progress/${postId}/materials/${matId}`,
        { status: next },
        authHeader(),
      );
    } catch {
      toast.error("Kunne ikke oppdatere status");
      load();
    }
  };

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  if (days.length === 0) {
    return (
      <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center">
        <Camera className="w-8 h-8 text-slate-300 mx-auto mb-2" />
        <div className="text-sm text-slate-500">Ingen progresjon publisert enda.</div>
        <div className="text-xs text-slate-400 mt-1">Gå til <a href="/doserne/progresjon" className="text-cyan-600 hover:underline">Daglig progresjon</a> for å legge til bilder.</div>
      </div>
    );
  }

  const shownDays = jobFilter
    ? days.map((d) => ({ ...d, posts: d.posts.filter((p) => p.job_id === jobFilter) })).filter((d) => d.posts.length)
    : days;

  return (
    <div className="space-y-5" data-testid="progress-timeline">
      {jobs.length > 0 && (
        <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1" data-testid="progress-job-filter">
          <button onClick={() => setJobFilter("")} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-black border transition ${jobFilter === "" ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`} data-testid="progress-job-filter-all">Alle</button>
          {jobs.map((j) => (
            <button key={j.id} onClick={() => setJobFilter(j.id)} className={`flex-shrink-0 px-3 py-1.5 rounded-full text-xs font-black border transition ${jobFilter === j.id ? "bg-cyan-600 text-white border-cyan-600" : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"}`} data-testid={`progress-job-filter-${j.id}`}>{j.title}</button>
          ))}
        </div>
      )}
      {shownDays.length === 0 ? (
        <div className="bg-white border-2 border-dashed border-slate-200 rounded-2xl p-8 text-center text-sm text-slate-500">Ingen innlegg for denne jobben.</div>
      ) : shownDays.map((d) => (
        <div key={d.date} className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
            <div className="text-sm font-bold text-slate-900 capitalize">{fmtDate(d.date)}</div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500">{d.posts.length} innlegg</div>
          </div>
          <div className="p-3 sm:p-4 space-y-4">
            {d.posts.map((post) => {
              const canDelete = isAdmin || post.created_by === currentUserId;
              return (
                <div key={post.id} className="border border-slate-200 rounded-xl p-3" data-testid={`progress-post-${post.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2 text-xs text-slate-600">
                      <div className="w-6 h-6 rounded-full bg-slate-900 flex items-center justify-center text-[10px] font-bold" style={{ color: "#fff" }}>
                        {(post.created_by_name || "?")[0]?.toUpperCase()}
                      </div>
                      <span className="font-semibold text-slate-900">{post.created_by_name || post.created_by_email}</span>
                      {post.job_title ? (
                        <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-black bg-cyan-50 text-cyan-700 border border-cyan-200 rounded-full px-1.5 py-0.5" data-testid={`post-job-${post.id}`}>{post.job_title}</span>
                      ) : null}
                    </div>
                    {canDelete && (
                      <button onClick={() => del(post.id)} className="text-xs text-red-500 hover:text-red-700 p-1" data-testid={`delete-post-${post.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                  {post.note && <p className="text-sm text-slate-700 mb-2 whitespace-pre-wrap">{post.note}</p>}
                  {(post.materials || []).length > 0 && (
                    <div className="mb-2 border border-slate-200 rounded-lg overflow-hidden" data-testid={`materials-${post.id}`}>
                      {post.material_list_id ? (
                        <button
                          type="button"
                          onClick={() => setMaterialListId(post.material_list_id)}
                          className="w-full px-3 py-1.5 bg-emerald-50 hover:bg-emerald-100 active:bg-emerald-200 border-b border-emerald-200 flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-black text-emerald-700 transition"
                          title="Åpne som materialliste (redigerbar av forfatter + admin)"
                          data-testid={`open-material-list-${post.id}`}
                        >
                          <Package className="w-3 h-3" />
                          <span className="flex-1 text-left">Materialer til i morgen · {(post.materials || []).length}</span>
                          <ExternalLink className="w-3 h-3 opacity-70" />
                        </button>
                      ) : (
                        <div className="px-3 py-1.5 bg-slate-50 border-b border-slate-200 flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-black text-slate-500">
                          <Package className="w-3 h-3" /> Materialer til i morgen · {(post.materials || []).length}
                        </div>
                      )}
                      <ul>
                        {(post.materials || []).map((m) => {
                          const status = m.status || "pending";
                          const style =
                            status === "delivered" ? { icon: Check, bg: "bg-emerald-100", text: "text-emerald-700", line: "line-through opacity-70", label: "Levert" }
                            : status === "ordered" ? { icon: Truck, bg: "bg-amber-100", text: "text-amber-700", line: "", label: "Bestilt" }
                            : { icon: Clock, bg: "bg-slate-100", text: "text-slate-500", line: "", label: "Venter" };
                          const Icon = style.icon;
                          return (
                            <li key={m.id} className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0">
                              <button
                                type="button"
                                onClick={() => cycleMaterial(post.id, m.id, status)}
                                title={`Trykk for å endre status (${style.label} → neste)`}
                                className={`w-6 h-6 rounded-md flex items-center justify-center flex-shrink-0 ${style.bg} ${style.text} hover:opacity-80 active:scale-90 transition`}
                                data-testid={`material-toggle-${m.id}`}
                              >
                                <Icon className="w-3.5 h-3.5" strokeWidth={2.5} />
                              </button>
                              <span className={`flex-1 text-sm text-slate-800 truncate ${style.line}`} data-testid={`material-name-${m.id}`}>{m.name}</span>
                              <span className={`text-[10px] uppercase font-black tracking-wide ${style.text}`} data-testid={`material-status-${m.id}`}>{style.label}</span>
                            </li>
                          );
                        })}
                      </ul>
                    </div>
                  )}
                  {post.photos?.length > 0 && (() => {
                    const urls = post.photos
                      .filter((ph) => ph && typeof ph.url === "string" && ph.url.trim())
                      .map((ph) => ph.url);
                    return (
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-1.5">
                      {urls.map((url, i) => (
                        <button
                          key={i}
                          onClick={() => setLightbox({ photos: urls, index: i })}
                          className="aspect-square bg-slate-100 rounded-lg overflow-hidden hover:opacity-90 active:opacity-70"
                          data-testid={`photo-${post.id}-${i}`}
                        >
                          <img
                            src={url}
                            alt=""
                            className="w-full h-full object-cover"
                            loading="lazy"
                            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }}
                          />
                        </button>
                      ))}
                    </div>
                    );
                  })()}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {lightbox && (
        <TimelineLightbox
          photos={lightbox.photos}
          index={lightbox.index}
          onIndex={(i) => setLightbox((lb) => (lb ? { ...lb, index: i } : lb))}
          onClose={() => setLightbox(null)}
        />
      )}

      {materialListId && (
        <MaterialListModal
          listId={materialListId}
          authHeader={authHeader}
          canDelete={isAdmin}
          onClose={() => { setMaterialListId(null); load(); }}
          onDeleted={() => { setMaterialListId(null); load(); }}
        />
      )}
    </div>
  );
}


function TimelineLightbox({ photos, index, onIndex, onClose }) {
  const total = photos.length;
  const touchX = React.useRef(null);

  const go = React.useCallback((dir) => {
    onIndex((index + dir + total) % total);
  }, [index, total, onIndex]);

  React.useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowLeft") go(-1);
      else if (e.key === "ArrowRight") go(1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  const onTouchStart = (e) => { touchX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchX.current == null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    if (Math.abs(dx) > 50 && total > 1) go(dx < 0 ? 1 : -1);
    touchX.current = null;
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
      onClick={onClose} onTouchStart={onTouchStart} onTouchEnd={onTouchEnd} data-testid="lightbox">
      <button onClick={onClose} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white z-10" data-testid="lightbox-close">
        <X className="w-6 h-6" />
      </button>
      <img
        src={photos[index]}
        alt=""
        className="max-w-full max-h-full object-contain select-none"
        onClick={(e) => e.stopPropagation()}
        data-testid="lightbox-image"
      />
      {total > 1 && (
        <>
          <button onClick={(e) => { e.stopPropagation(); go(-1); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="lightbox-prev">
            <ChevronLeft className="w-7 h-7" />
          </button>
          <button onClick={(e) => { e.stopPropagation(); go(1); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white" data-testid="lightbox-next">
            <ChevronRight className="w-7 h-7" />
          </button>
          <div className="absolute bottom-5 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/60 text-white text-sm font-semibold" data-testid="lightbox-counter">
            {index + 1} / {total}
          </div>
        </>
      )}
    </div>
  );
}
